<script setup lang="ts">
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { getSessionStorageReac } from "@/assets/js/helpers";
import { useDefaultStore } from "@/store";
import { useToast } from "primevue/usetoast";

definePage({
	name: "MusicBeatTheClockPage",
});

type TMusicAlbum = {
	id: string | number;
	artist_credit: string;
	cover_thumb_250_url: string;
	cover_thumb_500_url: string;
	cover_url: string;
	primary_type: "album";
	title: string;
};

type TFeedbackState = "idle" | "loading" | "correct" | "wrong";

const GAME_TOKEN_KEY = "game-token";

const store = useDefaultStore();
const router = useRouter();
const toast = useToast();

const albums = ref<TMusicAlbum[]>([]);
const isSubmitting = ref(false);
const isLocked = ref(false);
const isGameOver = ref(false);
const isExpiredGameOver = ref(false);
const hasReportedExpiration = ref(false);

const currentRound = ref(1);
const nextRound = ref(1);
const score = ref(0);

const feedbackState = ref<TFeedbackState>("idle");
const albumsVisible = ref(true);

const timeLeft = ref(0);
let timerInterval: ReturnType<typeof setInterval> | null = null;

const gameToken = getSessionStorageReac<string | null>(
	GAME_TOKEN_KEY,
	false,
	null,
);

// const preloadedImageMap = new Map<string, Promise<void>>();

function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

const failedImageUrls = ref<Record<string, true>>({});

function getRawAlbumImage(album: TMusicAlbum): string | null {
	return (
		album.cover_thumb_250_url ||
		album.cover_thumb_500_url ||
		album.cover_url ||
		null
	);
}

function isImageMarkedAsFailed(src: string | null): boolean {
	return Boolean(src && failedImageUrls.value[src]);
}

function markImageAsFailed(src: string | null) {
	if (!src || failedImageUrls.value[src]) {
		return;
	}

	failedImageUrls.value = {
		...failedImageUrls.value,
		[src]: true,
	};
}

function getAlbumImage(album: TMusicAlbum): string | null {
	const src = getRawAlbumImage(album);

	if (!src || isImageMarkedAsFailed(src)) {
		return null;
	}

	return src;
}

function hasAlbumImage(album: TMusicAlbum): boolean {
	return Boolean(getAlbumImage(album));
}

function handleAlbumImageError(album: TMusicAlbum) {
	markImageAsFailed(getRawAlbumImage(album));
}

function getAlbumPanelStyle(album: TMusicAlbum) {
	const image = getAlbumImage(album);

	return {
		"--album-cover-image": image ? `url("${image}")` : "none",
	};
}

function getAlbumInitial(album: TMusicAlbum): string {
	return (album.title || "?").trim().charAt(0).toUpperCase();
}

function preloadImage(src: string): Promise<boolean> {
	return new Promise((resolve) => {
		if (!src) {
			resolve(false);
			return;
		}

		const img = new Image();

		img.onload = () => resolve(true);
		img.onerror = () => resolve(false);
		img.src = src;
	});
}

async function preloadAlbumImages(targetAlbums: TMusicAlbum[]) {
	const uniqueUrls = [
		...new Set(
			targetAlbums
				.map((album) => getRawAlbumImage(album))
				.filter((src): src is string => Boolean(src)),
		),
	];

	const results = await Promise.all(
		uniqueUrls.map(async (src) => {
			if (isImageMarkedAsFailed(src)) {
				return;
			}

			const ok = await preloadImage(src);

			if (!ok) {
				markImageAsFailed(src);
			}
		}),
	);

	return results;
}

// function getAlbumImage(album: TMusicAlbum) {
// 	return (
// 		album.cover_thumb_250_url ||
// 		album.cover_thumb_500_url ||
// 		album.cover_url ||
// 		""
// 	);
// }

// function getAlbumPanelStyle(album: TMusicAlbum) {
// 	const image = getAlbumImage(album);

// 	return {
// 		"--album-cover-image": image ? `url("${image}")` : "none",
// 	};
// }

// function preloadImage(src: string): Promise<void> {
// 	if (!src) {
// 		return Promise.resolve();
// 	}

// 	const existingPromise = preloadedImageMap.get(src);

// 	if (existingPromise) {
// 		return existingPromise;
// 	}

// 	const promise = new Promise<void>((resolve) => {
// 		const img = new Image();

// 		img.decoding = "async";
// 		img.onload = () => resolve();
// 		img.onerror = () => resolve();
// 		img.src = src;
// 	});

// 	preloadedImageMap.set(src, promise);

// 	return promise;
// }

// async function preloadAlbumImages(targetAlbums: TMusicAlbum[]) {
// 	const urls = [
// 		...new Set(
// 			targetAlbums.map((album) => getAlbumImage(album)).filter(Boolean),
// 		),
// 	];

// 	await Promise.all(urls.map(preloadImage));
// }

function clearTimer() {
	if (timerInterval) {
		clearInterval(timerInterval);
		timerInterval = null;
	}
	timeLeft.value = 0;
}

function startTimer(seconds: number) {
	clearTimer();

	timeLeft.value = Math.max(0, Math.ceil(seconds));

	if (timeLeft.value <= 0) {
		void expireGame();
		return;
	}

	timerInterval = setInterval(() => {
		if (timeLeft.value > 0) {
			timeLeft.value -= 1;
		}

		if (timeLeft.value <= 0) {
			clearTimer();
			void expireGame();
		}
	}, 1000);
}

function isTimeExpiredFromApi(data: any): boolean {
	return (
		data?.isExpired === true ||
		data?.timeExpired === true ||
		data?.secondsLeft === 0
	);
}

function isFinishedFromApi(data: any): boolean {
	return data?.isFinished === true || isTimeExpiredFromApi(data);
}

async function expireGame(showPlayAgain = true) {
	if (isGameOver.value) {
		return;
	}

	isGameOver.value = true;
	isExpiredGameOver.value = showPlayAgain;
	isLocked.value = true;
	feedbackState.value = "wrong";
	clearTimer();

	const currentGameToken = gameToken.value;

	if (!currentGameToken || hasReportedExpiration.value) {
		return;
	}

	hasReportedExpiration.value = true;

	try {
		await api.postMusicBTCGameExpired({
			token: currentGameToken,
		});
	} catch (err: any) {
		console.warn(err.message);
	}
}

function resetGameState() {
	albums.value = [];
	isSubmitting.value = false;
	isLocked.value = false;
	isGameOver.value = false;
	isExpiredGameOver.value = false;
	hasReportedExpiration.value = false;
	currentRound.value = 1;
	nextRound.value = 1;
	score.value = 0;
	feedbackState.value = "idle";
	albumsVisible.value = true;
	clearTimer();
}

async function playAgain() {
	gameToken.value = null;
	resetGameState();
	await getInitialPair();
}

async function initializeGame() {
	const existingGameToken = gameToken.value;

	if (existingGameToken) {
		await getMusicSinglePayerState(existingGameToken);
		return;
	}

	await getInitialPair();
}

async function getInitialPair() {
	try {
		const res = await api.getMusicInitialPair({
			token: store.userData?.usr_token ?? "",
			type: store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.albums)) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: data?.showText ? data?.message : null,
				life: 3500,
			});

			await router.push({
				name: "MusicPage",
			});

			return;
		}

		const returnedGameToken = data.token ?? null;

		if (returnedGameToken) {
			gameToken.value = returnedGameToken;
		}

		preloadAlbumImages(data.albums);

		albums.value = data.albums;
		currentRound.value = 1;
		nextRound.value = data.nextRound ?? 1;
		score.value = data.score ?? 0;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

		if (isFinishedFromApi(data)) {
			await expireGame(isTimeExpiredFromApi(data));
		}
	} catch (err: any) {
		console.warn(err.message);
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText ? customErrorMsg : null,
			life: 3500,
		});

		await router.push({
			name: "MusicPage",
		});
	}
}

async function getMusicSinglePayerState(currentGameToken: string) {
	try {
		const res = await api.getMusicSinglePayerState({
			token: currentGameToken,
			type: store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.albums)) {
			gameToken.value = null;
			await getInitialPair();
			return;
		}

		preloadAlbumImages(data.albums);

		albums.value = data.albums;
		currentRound.value = data.round ?? 1;
		nextRound.value = (data.round ?? 1) + 1;
		score.value = data.score ?? 0;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

		if (isFinishedFromApi(data)) {
			await expireGame(isTimeExpiredFromApi(data));
		}
	} catch (err: any) {
		console.warn(err.message);
		gameToken.value = null;
		await getInitialPair();
	}
}

async function animateAnswerTransition(
	nextAlbums: TMusicAlbum[],
	resolvedNextRound: number,
	state: "correct" | "wrong",
) {
	feedbackState.value = state;

	await sleep(500);

	albumsVisible.value = false;

	await sleep(260);

	albums.value = nextAlbums;
	currentRound.value = resolvedNextRound;
	nextRound.value = resolvedNextRound;

	await nextTick();

	albumsVisible.value = true;

	await sleep(260);

	if (!isGameOver.value) {
		feedbackState.value = "idle";
	}
}

async function handleAnswer(clickedIndex: 0 | 1) {
	if (albums.value.length !== 2 || isSubmitting.value || isLocked.value) {
		return;
	}

	const vote = albums.value[clickedIndex].id;
	const existingGameToken = gameToken.value;

	try {
		isSubmitting.value = true;
		feedbackState.value = "loading";

		const res = await api.postMusicBTCHandleAnswer({
			vote,
			token: existingGameToken ?? "",
		});

		const data = res.data.data;

		if (!data?.ok) {
			feedbackState.value = "idle";
			return;
		}

		score.value = data.score ?? score.value;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

		if (isFinishedFromApi(data)) {
			await expireGame(isTimeExpiredFromApi(data));
			return;
		}

		if (Array.isArray(data.albums) && data.albums.length === 2) {
			const resolvedNextRound = data.nextRound ?? currentRound.value + 1;

			preloadAlbumImages(data.albums);

			await animateAnswerTransition(
				data.albums,
				resolvedNextRound,
				data.isCorrect ? "correct" : "wrong",
			);
			return;
		}

		feedbackState.value = "idle";
	} catch (err: any) {
		console.warn(err.message);
		feedbackState.value = "idle";
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText ? customErrorMsg : null,
			life: 3500,
		});

		await router.push({
			name: "MusicPage",
		});
	} finally {
		isSubmitting.value = false;
	}
}

onMounted(() => {
	initializeGame();
});

onBeforeUnmount(() => {
	clearTimer();
});

useHead({
	title: getPageTitle("Music - Beat The Clock"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Music - Beat The Clock"),
		},
		{
			name: "description",
			content:
				"Beat the timer, make quick choices, and guess which album came first.",
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("Music - Beat The Clock"),
		},
		{
			property: "og:description",
			content:
				"Beat the timer, make quick choices, and guess which album came first.",
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
	link: [
		{
			rel: "preconnect",
			href: "https://coverartarchive.org",
			crossorigin: "anonymous",
		},
		{
			rel: "dns-prefetch",
			href: "https://coverartarchive.org",
		},
	],
});
</script>

<template>
	<section
		class="music-btc-game-page"
		:class="{
			'music-btc-game-page--correct': feedbackState === 'correct',
			'music-btc-game-page--wrong': feedbackState === 'wrong',
		}"
	>
		<div class="game-canvas">
			<RouterLink
				:to="{ name: 'MusicPage' }"
				class="category-back-link"
			>
				← Back
			</RouterLink>

			<div class="score-badge">{{ score }} pts</div>

			<div
				v-if="albums.length === 2"
				class="timer-badge"
				:class="{ 'is-low': timeLeft <= 10 }"
			>
				{{ timeLeft }}s
			</div>

			<div
				v-if="albums.length === 2 && currentRound === 1"
				class="instruction-badge"
			>
				Which album came first?
			</div>

			<div
				v-if="albums.length === 2"
				class="vs-badge"
				:class="{
					'is-loading': feedbackState === 'loading',
					'is-correct': feedbackState === 'correct',
					'is-wrong': feedbackState === 'wrong',
				}"
			>
				<span v-if="feedbackState === 'correct'">✓</span>
				<span v-else-if="feedbackState === 'wrong'">✕</span>
				<span v-else-if="feedbackState === 'loading'">...</span>
				<span v-else>VS</span>
			</div>

			<div
				v-if="albums.length === 2 && isExpiredGameOver"
				class="game-over-actions"
			>
				<button
					type="button"
					class="play-again-button"
					aria-label="Play again"
					title="Play again"
					@click="playAgain"
				>
					<i-fa-rotate-right
						class="play-again-button__icon"
						aria-hidden="true"
					/>
				</button>
			</div>

			<div
				v-if="albums.length === 2"
				class="panels-grid"
			>
				<div
					v-for="(album, index) in albums"
					:key="`${album.title}-${index}`"
					class="album-panel"
					:class="{
						'is-disabled': isSubmitting || isLocked,
						'is-fading': !albumsVisible,
					}"
					:style="getAlbumPanelStyle(album)"
					@click="handleAnswer(index as 0 | 1)"
				>
					<div class="album-panel__bg"></div>
					<div class="album-panel__overlay"></div>
					<div class="album-panel__tint"></div>

					<div class="album-panel__content">
						<div class="album-card">
							<!-- <div class="album-card__cover">
								<img
									v-if="getAlbumImage(album)"
									:src="getAlbumImage(album)"
									:alt="`${album.title} cover`"
									loading="eager"
									decoding="async"
									fetchpriority="high"
								/>
							</div> -->

							<div
								class="album-card__cover"
								:class="{ 'is-fallback': !hasAlbumImage(album) }"
							>
								<img
									v-if="hasAlbumImage(album)"
									:src="getAlbumImage(album)!"
									:alt="`${album.title} cover`"
									loading="eager"
									decoding="async"
									fetchpriority="high"
								/>

								<div
									v-else
									class="album-card__cover-fallback"
									aria-hidden="true"
								>
									<div class="album-card__cover-icon">♪</div>
								</div>
							</div>

							<div class="album-card__meta">
								<h2 class="album-card__title">
									{{ album.title }}
								</h2>

								<p class="album-card__artist">
									{{ album.artist_credit }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				v-else
				class="loading-state"
			>
				<div class="loading-badge">
					<div class="loading-spinner"></div>
				</div>
			</div>

			<div class="attribution-badge">
				Cover art provided via the Cover Art Archive, a joint project of
				Internet Archive and MusicBrainz. Images © their respective copyright
				owners.
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.music-btc-game-page {
	--pad: 16px;
	--safe-top: env(safe-area-inset-top, 0px);
	--safe-right: env(safe-area-inset-right, 0px);
	--safe-bottom: env(safe-area-inset-bottom, 0px);
	--safe-left: env(safe-area-inset-left, 0px);

	position: relative;
	width: 100%;
	height: 100dvh;
	background: rgb(13, 16, 21);
	overflow: hidden;

	&--correct {
		.album-panel__tint {
			background: rgba(36, 130, 72, 0.12);
		}
	}

	&--wrong {
		.album-panel__tint {
			background: rgba(170, 50, 50, 0.14);
		}
	}
}

.game-canvas {
	position: absolute;
	inset: 0;
	padding: calc(var(--pad) + var(--safe-top))
		calc(var(--pad) + var(--safe-right)) calc(var(--pad) + var(--safe-bottom))
		calc(var(--pad) + var(--safe-left));
	box-sizing: border-box;
}

.back-link,
.score-badge,
.timer-badge,
.attribution-badge,
.instruction-badge {
	position: absolute;
	z-index: 8;
	border-radius: 999px;
	backdrop-filter: blur(6px);
}

.back-link,
.score-badge {
	top: calc(var(--pad) + var(--safe-top));
	padding: 8px 12px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 13px;
	font-weight: 600;
	line-height: 1;
	background: rgba(0, 0, 0, 0.22);
	color: rgba(255, 255, 255, 0.78);
	transition:
		background 0.2s ease,
		color 0.2s ease,
		opacity 0.2s ease;
}

.back-link {
	left: calc(var(--pad) + var(--safe-left));
	text-decoration: none;

	&:hover {
		background: rgba(24, 29, 38, 0.86);
	}
}

.score-badge {
	right: calc(var(--pad) + var(--safe-right));
	pointer-events: none;
}

.timer-badge {
	left: 50%;
	top: calc(var(--pad) + var(--safe-top) + 40px);
	transform: translateX(-50%);
	padding: 10px 16px;
	background: rgba(18, 22, 29, 0.9);
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: rgb(255, 255, 255);
	font-size: 18px;
	font-weight: 900;
	line-height: 1;
	letter-spacing: 0.03em;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);

	&.is-low {
		background: rgba(160, 36, 36, 0.92);
	}
}

.attribution-badge {
	left: 50%;
	transform: translateX(-50%);
	right: calc(var(--pad) + var(--safe-right));
	bottom: calc(var(--pad) + var(--safe-bottom));
	pointer-events: auto;
	padding: 6px 18px;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	width: calc(100vw - 16px);
}

.instruction-badge {
	left: 50%;
	top: calc(50% - 112px);
	transform: translateX(-50%);
	padding: 6px 10px;
	border: none;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	backdrop-filter: blur(6px);
	max-width: calc(100% - 32px);
	pointer-events: none;
}

.panels-grid,
.loading-state {
	position: absolute;
	inset: 0;
}

.panels-grid {
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr 1fr;
}

.album-panel {
	position: relative;
	min-height: 0;
	overflow: hidden;
	background: rgb(20, 25, 34);
	cursor: pointer;
	opacity: 1;
	transform: scale(1);
	transition:
		opacity 0.26s ease,
		transform 0.26s ease;

	&.is-disabled {
		pointer-events: none;
	}

	&.is-fading {
		opacity: 0;
		transform: scale(1.01);
	}
}

/* stylelint-disable-next-line no-descending-specificity */
.album-panel__bg,
.album-panel__overlay,
.album-panel__tint {
	position: absolute;
	inset: 0;
}

.album-panel__bg {
	background-image: var(--album-cover-image);
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	transform: scale(1.01);
	filter: blur(1.5px);
	opacity: 0.34;
}

.album-panel__overlay {
	background: linear-gradient(
			180deg,
			rgba(11, 14, 19, 0.56) 0%,
			rgba(11, 14, 19, 0.7) 100%
		),
		radial-gradient(
			circle at center,
			rgba(255, 255, 255, 0.025) 0%,
			rgba(255, 255, 255, 0) 62%
		);
	transition: background 0.2s ease;
}

/* stylelint-disable-next-line no-descending-specificity */
.album-panel__tint {
	background: transparent;
	transition: background 0.22s ease;
}

.album-panel__content {
	position: relative;
	z-index: 1;
	width: 100%;
	height: 100%;
	padding: 14px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
}

.album-card {
	display: grid;
	grid-template-columns: minmax(104px, 38%) minmax(0, 1fr);
	align-items: center;
	gap: 12px;
	width: min(100%, 360px);
	padding: 12px;
	box-sizing: border-box;
	border-radius: 20px;
	background: rgba(24, 29, 38, 0.42);
	border: 1px solid rgba(255, 255, 255, 0.08);
	box-shadow:
		0 10px 22px rgba(0, 0, 0, 0.2),
		inset 0 1px 0 rgba(255, 255, 255, 0.04);
	transition:
		background 0.2s ease,
		border-color 0.2s ease;
}

.album-panel:hover .album-card {
	background: rgba(30, 36, 46, 0.5);
	border-color: rgba(255, 255, 255, 0.14);
}

.album-panel:hover .album-panel__overlay {
	background: linear-gradient(
			180deg,
			rgba(11, 14, 19, 0.5) 0%,
			rgba(11, 14, 19, 0.64) 100%
		),
		radial-gradient(
			circle at center,
			rgba(255, 255, 255, 0.035) 0%,
			rgba(255, 255, 255, 0) 62%
		);
}

.album-card__cover {
	width: 100%;
	aspect-ratio: 1 / 1;
	border-radius: 14px;
	overflow: hidden;
	background: rgba(255, 255, 255, 0.05);
	box-shadow:
		0 8px 18px rgba(0, 0, 0, 0.18),
		0 0 0 1px rgba(255, 255, 255, 0.06);
}

.album-card__cover img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.album-card__cover.is-fallback {
	position: relative;
	background: linear-gradient(
		180deg,
		rgba(42, 48, 61, 0.92) 0%,
		rgba(26, 30, 39, 0.96) 100%
	);
}

.album-card__cover-fallback {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 6px;
	color: rgba(255, 255, 255, 0.78);
}

.album-card__cover-icon {
	font-size: clamp(20px, 4vw, 28px);
	line-height: 1;
	opacity: 0.73;
}

.album-card__meta {
	min-width: 0;
	text-align: left;
}

.album-card__title {
	margin: 0;
	color: rgb(255, 255, 255);
	font-size: clamp(18px, 4.6vw, 24px);
	font-weight: 800;
	line-height: 1.14;
	letter-spacing: -0.02em;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

.album-card__artist {
	margin: 7px 0 0;
	color: rgba(255, 255, 255, 0.72);
	font-size: clamp(13px, 3.5vw, 15px);
	font-weight: 500;
	line-height: 1.28;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

.vs-badge,
.loading-badge {
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	z-index: 7;
	width: 84px;
	height: 84px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgb(243, 243, 243);
	color: rgb(17, 17, 17);
	font-size: 24px;
	font-weight: 900;
	box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
	pointer-events: none;
}

.vs-badge {
	transition:
		background 0.22s ease,
		color 0.22s ease,
		transform 0.22s ease,
		box-shadow 0.22s ease;
}

.vs-badge.is-loading {
	transform: translate(-50%, -50%) scale(0.96);
}

.vs-badge.is-correct {
	background: rgb(49, 180, 90);
	color: rgb(255, 255, 255);
	box-shadow: 0 10px 26px rgba(49, 180, 90, 0.28);
}

.vs-badge.is-wrong {
	background: rgb(208, 62, 62);
	color: rgb(255, 255, 255);
	box-shadow: 0 10px 26px rgba(208, 62, 62, 0.28);
}

.game-over-actions {
	position: absolute;
	left: 50%;
	top: calc(50% + 54px);
	transform: translateX(-50%);
	z-index: 8;
	display: flex;
	justify-content: center;
}

.play-again-button {
	width: 42px;
	height: 42px;
	padding: 0;
	border-radius: 50%;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(18, 22, 29, 0.84);
	color: rgb(255, 255, 255);
	cursor: pointer;
	box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.loading-spinner {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	border: 4px solid rgba(17, 17, 17, 0.14);
	border-top-color: rgb(17, 17, 17);
	animation: loading-spin 0.85s linear infinite;
}

@keyframes loading-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (min-width: 768px) {
	.music-btc-game-page {
		--pad: 20px;
	}

	.timer-badge {
		top: calc(var(--pad) + var(--safe-top) + 44px);
	}

	.instruction-badge {
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		max-width: min(440px, calc(100% - 180px));
		font-size: 11px;
		padding: 6px 12px;
	}

	.panels-grid {
		grid-template-columns: 1fr 1fr;
		grid-template-rows: 1fr;
	}

	.album-card {
		width: min(100%, 304px);
		grid-template-columns: 1fr;
		grid-template-rows: auto auto;
		justify-items: stretch;
		padding: 14px;
		gap: 14px;
	}

	.album-card__cover {
		width: 100%;
		max-width: none;
	}

	.album-card__meta {
		text-align: center;
	}

	.album-card__title {
		font-size: clamp(22px, 2vw, 28px);
	}

	.album-card__artist {
		font-size: clamp(14px, 1.1vw, 16px);
	}

	.attribution-badge {
		width: auto;
		max-width: min(720px, calc(100% - 32px));
	}

	.vs-badge,
	.loading-badge {
		width: 78px;
		height: 78px;
		font-size: 22px;
	}

	.game-over-actions {
		top: 50%;
		left: calc(50% + 58px);
		transform: translateY(-50%);
	}
}
</style>

<!-- <template>
	<section
		v-if="albums.length === 2"
		class="music-btc-game-page"
		:class="{
			'music-btc-game-page--correct': feedbackState === 'correct',
			'music-btc-game-page--wrong': feedbackState === 'wrong',
			'music-btc-game-page--loading-state': feedbackState === 'loading',
		}"
	>
		<RouterLink
			:to="{ name: 'MusicPage' }"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<div class="score-badge">{{ score }} pts</div>

		<div
			class="timer-badge"
			:class="{ 'is-low': timeLeft <= 10 }"
		>
			{{ timeLeft }}s
		</div>

		<div
			v-if="currentRound === 1"
			class="instruction-badge"
		>
			Which album came first?
		</div>

		<div
			v-for="(album, index) in albums"
			:key="`${album.title}-${index}`"
			class="album-panel"
			:class="{
				'is-disabled': isSubmitting || isLocked,
				'is-fading': !albumsVisible,
			}"
			:style="getAlbumPanelStyle(album)"
			@click="handleAnswer(index as 0 | 1)"
		>
			<div class="album-panel__bg"></div>
			<div class="album-panel__shade"></div>
			<div class="album-panel__tint"></div>

			<div class="album-panel__content">
				<div class="album-card">
					<div class="album-card__cover">
						<img
							v-if="getAlbumImage(album)"
							:src="getAlbumImage(album)"
							:alt="`${album.title} cover`"
							loading="eager"
							decoding="async"
							fetchpriority="high"
						/>
					</div>

					<div class="album-card__meta">
						<h2 class="album-card__title">
							{{ album.title }}
						</h2>

						<p class="album-card__artist">
							{{ album.artist_credit }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<div
			class="vs-badge"
			:class="{
				'is-loading': feedbackState === 'loading',
				'is-correct': feedbackState === 'correct',
				'is-wrong': feedbackState === 'wrong',
			}"
		>
			<span v-if="feedbackState === 'correct'">✓</span>
			<span v-else-if="feedbackState === 'wrong'">✕</span>
			<span v-else-if="feedbackState === 'loading'">...</span>
			<span v-else>VS</span>
		</div>

		<div
			v-if="isGameOver"
			class="game-over-actions"
		>
			<button
				type="button"
				class="play-again-button"
				aria-label="Play again"
				title="Play again"
				@click="playAgain"
			>
				<i-fa-rotate-right
					class="play-again-button__icon"
					aria-hidden="true"
				/>
			</button>
		</div>
	</section>

	<section
		v-else
		class="music-btc-game-page music-btc-game-page--loading"
	>
		<div class="loading-badge">
			<div class="loading-spinner"></div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.music-btc-game-page {
	position: relative;
	width: 100%;
	min-height: 100dvh;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr 1fr;
	background: #0d0f14;
	overflow: hidden;

	&--correct {
		.album-panel__tint {
			background: rgba(36, 130, 72, 0.18);
		}
	}

	&--wrong {
		.album-panel__tint {
			background: rgba(170, 50, 50, 0.2);
		}
	}
}

.back-link,
.score-badge {
	position: absolute;
	z-index: 8;
	top: 16px;
	padding: 9px 13px;
	border-radius: 999px;
	background: rgba(15, 18, 24, 0.72);
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.92);
	font-size: 13px;
	font-weight: 600;
	line-height: 1;
	backdrop-filter: blur(8px);
}

.back-link {
	left: 16px;
	text-decoration: none;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;

	&:hover {
		background: rgba(20, 24, 31, 0.9);
		border-color: rgba(255, 255, 255, 0.14);
		// transform: translateY(-1px);
	}
}

.score-badge {
	right: 16px;
	pointer-events: none;
}

.timer-badge {
	position: absolute;
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 8;
	padding: 10px 16px;
	border-radius: 999px;
	background: rgba(18, 22, 29, 0.9);
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: rgb(255, 255, 255);
	font-size: 18px;
	font-weight: 900;
	letter-spacing: 0.03em;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);

	&.is-low {
		background: rgba(160, 36, 36, 0.92);
	}
}

.instruction-badge {
	position: absolute;
	top: calc(50% - 112px);
	left: 50%;
	transform: translateX(-50%);
	z-index: 8;
	padding: 6px 10px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	backdrop-filter: blur(6px);
	max-width: calc(100% - 32px);
	pointer-events: none;
}

.album-panel {
	position: relative;
	min-height: 50dvh;
	background: #131722;
	cursor: pointer;
	overflow: hidden;
	opacity: 1;
	transform: scale(1);
	transition:
		opacity 0.32s ease,
		transform 0.32s ease,
		filter 0.32s ease;

	&.is-disabled {
		pointer-events: none;
	}

	&.is-fading {
		opacity: 0;
		transform: scale(1.01);
	}
}

/* stylelint-disable-next-line no-descending-specificity */
.album-panel__bg,
.album-panel__shade,
.album-panel__tint {
	position: absolute;
	inset: 0;
}

.album-panel__bg {
	background-image: var(--album-cover-image);
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	transform: scale(1.08);
	filter: blur(14px);
	opacity: 0.32;
}

.album-panel__shade {
	background: linear-gradient(
			180deg,
			rgba(10, 12, 16, 0.52) 0%,
			rgba(10, 12, 16, 0.74) 100%
		),
		radial-gradient(
			circle at center,
			rgba(255, 255, 255, 0.03) 0%,
			rgba(255, 255, 255, 0) 58%
		);
}

/* stylelint-disable-next-line no-descending-specificity */
.album-panel__tint {
	background: transparent;
	transition: background 0.25s ease;
}

.album-panel__content {
	position: relative;
	z-index: 2;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px 24px;
}

.album-card {
	width: min(100%, 320px);
	padding: 24px;
	border-radius: 24px;
	background: rgba(22, 26, 34, 0.9);
	border: 1px solid rgba(255, 255, 255, 0.08);
	box-shadow:
		0 18px 40px rgba(0, 0, 0, 0.3),
		0 1px 0 rgba(255, 255, 255, 0.04) inset;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	transition:
		transform 0.2s ease,
		border-color 0.2s ease,
		background 0.2s ease;
}

.album-panel:hover .album-card {
	// transform: translateY(-2px);
	border-color: rgba(255, 255, 255, 0.12);
	background: rgba(24, 29, 38, 0.94);
}

.album-card__cover {
	width: 200px;
	height: 200px;
	border-radius: 18px;
	overflow: hidden;
	background: rgba(255, 255, 255, 0.04);
	box-shadow:
		0 12px 28px rgba(0, 0, 0, 0.28),
		0 0 0 1px rgba(255, 255, 255, 0.06);
	flex: 0 0 auto;
}

.album-card__cover img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.album-card__meta {
	width: 100%;
	min-width: 0;
	margin-top: 18px;
}

.album-card__title {
	margin: 0;
	color: rgb(255, 255, 255);
	font-size: 24px;
	font-weight: 800;
	line-height: 1.18;
	letter-spacing: -0.02em;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

.album-card__artist {
	margin: 10px 0 0;
	color: rgba(255, 255, 255, 0.68);
	font-size: 15px;
	font-weight: 500;
	line-height: 1.35;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.vs-badge,
.loading-badge {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%) scale(1);
	z-index: 7;
	width: 78px;
	height: 78px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f5f5f5;
	color: rgb(17, 17, 17);
	font-size: 22px;
	font-weight: 900;
	box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
	pointer-events: none;
	transition:
		background 0.25s ease,
		color 0.25s ease,
		transform 0.25s ease,
		box-shadow 0.25s ease;
}

.vs-badge.is-loading {
	transform: translate(-50%, -50%) scale(0.96);
}

.vs-badge.is-correct {
	background: rgb(49, 180, 90);
	color: rgb(255, 255, 255);
	transform: translate(-50%, -50%) scale(1.06);
	box-shadow: 0 12px 32px rgba(49, 180, 90, 0.3);
}

.vs-badge.is-wrong {
	background: rgb(208, 62, 62);
	color: rgb(255, 255, 255);
	transform: translate(-50%, -50%) scale(1.06);
	box-shadow: 0 12px 32px rgba(208, 62, 62, 0.32);
}

.loading-spinner {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 4px solid rgba(17, 17, 17, 0.14);
	border-top-color: rgb(17, 17, 17);
	animation: loading-spin 0.85s linear infinite;
}

.game-over-actions {
	position: absolute;
	left: 50%;
	top: calc(50% + 72px);
	transform: translateX(-50%);
	z-index: 7;
	display: flex;
	justify-content: center;
}

.play-again-button {
	outline: 0;
	width: 42px;
	height: 42px;
	padding: 0;
	border-radius: 50%;
	background: rgba(18, 22, 29, 0.88);
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: rgb(255, 255, 255);
	cursor: pointer;
	box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		transform 0.2s ease,
		background 0.2s ease,
		border-color 0.2s ease;

	&:hover {
		// transform: translateY(-1px);
		background: rgba(24, 29, 38, 0.96);
		border-color: rgba(255, 255, 255, 0.12);
	}
}

.play-again-button__icon {
	width: 20px;
	height: 20px;
	display: block;
}

@keyframes loading-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (min-width: 768px) {
	.music-btc-game-page {
		grid-template-columns: 1fr 1fr;
		grid-template-rows: 1fr;
	}

	.album-panel {
		min-height: 100dvh;
	}

	.back-link,
	.score-badge {
		top: 20px;
	}

	.back-link {
		left: 20px;
	}

	.score-badge {
		right: 20px;
	}

	.timer-badge {
		top: 72px;
	}

	.instruction-badge {
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		max-width: min(440px, calc(100% - 180px));
		font-size: 11px;
		padding: 6px 12px;
	}

	.album-panel__content {
		padding: 36px 28px;
	}

	.album-card {
		width: min(100%, 340px);
		padding: 26px;
	}

	.album-card__cover {
		width: 220px;
		height: 220px;
	}

	.album-card__title {
		font-size: 26px;
	}

	.album-card__artist {
		font-size: 16px;
	}

	.game-over-actions {
		top: 50%;
		left: calc(50% + 108px);
		transform: translateY(-50%);
		justify-content: flex-start;
	}
}
</style> -->
